import React, { useMemo } from "react";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  ListItemText,
  Checkbox,
  Chip,
  FormHelperText,
  makeStyles,
} from "@material-ui/core";

const useStyles = makeStyles({
  display: {
    minHeight: 24,
    display: "flex",
    alignItems: "center",
  },
  chips: {
    display: "flex",
    flexWrap: "wrap",
    gap: "4px",
    paddingRight: "24px",
  },
  label: {
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "calc(100% - 24px)",
    "&.MuiInputLabel-shrink": {
      width: "max-content", // lifts the absolute-positioning width cap
      maxWidth: "calc(133% - 32px)",
      pointerEvents: "auto", // enables the title tooltip
    },
  },
});

// getContentAnchorEl:null keeps the menu pinned below the field instead of
// re-centering on the selected item (which makes it jump as you select).
const MENU_PROPS = {
  getContentAnchorEl: null,
  anchorOrigin: { vertical: "bottom", horizontal: "left" },
  transformOrigin: { vertical: "top", horizontal: "left" },
  style: { zIndex: 1600 },
};

const SELECT_ALL_KEY = "__select_all__";

export default function MultiSelectChipsDropdown({
  id,
  label,
  options,
  value,
  onChange,
  selectAllLabel = null,
  deselectAllLabel = null,
  helperText = null,
  error = false,
}) {
  const classes = useStyles();
  const nameByKey = useMemo(() => options.reduce((acc, o) => {
    acc[o.key] = o.name;
    return acc;
  }, {}), [options]);
  const selectedKeys = useMemo(() => new Set(value), [value]);
  const allSelected = options.length > 0 && options.every((o) => selectedKeys.has(o.key));
  const showSelectAll = Boolean(selectAllLabel) && options.length > 1;

  const handleChange = (e) => {
    const nextValue = e.target.value;
    if (nextValue.includes(SELECT_ALL_KEY)) {
      onChange(allSelected ? [] : options.map((o) => o.key));
      return;
    }
    onChange(nextValue);
  };

  return (
    <FormControl variant="outlined" size="small" fullWidth error={error}>
      <InputLabel className={classes.label} title={label}>
        {label}
      </InputLabel>
      <Select
        id={id}
        multiple
        label={label}
        value={value}
        onChange={handleChange}
        classes={{ root: classes.display }}
        renderValue={(selected) => (
          <div className={classes.chips}>
            {selected.map((key) => (
              <Chip key={key} size="small" label={nameByKey[key] ?? key} />
            ))}
          </div>
        )}
        MenuProps={MENU_PROPS}
      >
        {showSelectAll && (
          <MenuItem key={SELECT_ALL_KEY} value={SELECT_ALL_KEY}>
            <Checkbox
              size="small"
              color="primary"
              checked={allSelected}
              indeterminate={!allSelected && value.length > 0}
            />
            <ListItemText primary={allSelected ? deselectAllLabel ?? selectAllLabel : selectAllLabel} />
          </MenuItem>
        )}
        {options.map((o) => (
          <MenuItem key={o.key} value={o.key}>
            <Checkbox size="small" color="primary" checked={selectedKeys.has(o.key)} />
            <ListItemText primary={o.name} />
          </MenuItem>
        ))}
      </Select>
      {helperText && <FormHelperText error={error}>{helperText}</FormHelperText>}
    </FormControl>
  );
}
